import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DELETE_MY_AI_ASSISTANT_CONVERSATION,
  MY_AI_ASSISTANT_CONVERSATION,
  RESOLVE_AI_ASSISTANT_ACTION,
  SEND_AI_ASSISTANT_MESSAGE,
  STOP_AI_ASSISTANT_TURN,
} from '@/queries/aiAssistant';
import type { AssistantAttachment, AssistantConversation } from '@/lib/ai-assistant/types';
import { planAssistantEffects } from '@/lib/ai-assistant/planAssistantEffects';
import { isEditingSurface } from '@/lib/ai-assistant/isEditingSurface';
import { assistantErrorCode } from '@/lib/ai-assistant/mapAssistantError';
import { resolveConversationView } from '@/lib/ai-assistant/resolveConversationView';
import { useAssistantPanel } from '@/contexts/AssistantContext';

const POLL_INTERVAL_MS = 1500;
// Codes after which the stored conversation id is useless (deleted, another
// account's id in this browser, or the flag was revoked).
const RESET_CODES = new Set(['not_found', 'feature_disabled']);

interface Options {
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  onError: (error: unknown) => void;
}

export function useAssistantConversation({ conversationId, setConversationId, onError }: Options) {
  const client = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    buildClientContext, markConversationOwned, ownsConversation, runClientCalls,
    cacheConversation, cachedConversation,
  } = useAssistantPanel();
  // A chat that remounts (page change, AI Studio replacing the sidebar) starts
  // from the conversation already loaded instead of a loading skeleton.
  const [seed] = useState(() => cachedConversation(conversationId));
  const [conversation, setConversation] = useState<AssistantConversation | null>(seed);
  const [reloadSuggested, setReloadSuggested] = useState(false);
  // The message being sent, shown in the chat before the server confirms it.
  const [pending, setPending] = useState<{ text: string; attachments: AssistantAttachment[] } | null>(null);
  // Messages whose effects were already applied (or predate this panel). The
  // cached seed is only shown: the first fetch is still "first sight", so what
  // a turn did while no chat was mounted (e.g. the panel was closed) is never
  // replayed when it reopens.
  const knownIds = useRef<{ conversationId: string | null; ids: Set<string> }>({
    conversationId: null,
    ids: new Set(),
  });

  const {
    data, error, startPolling, stopPolling, refetch,
  } = useQuery<{
    myAiAssistantConversation: AssistantConversation;
  }>(MY_AI_ASSISTANT_CONVERSATION, {
    variables: { id: conversationId },
    skip: !conversationId,
  });

  const [sendMutation, { loading: sending }] = useMutation<{
    sendAiAssistantMessage: AssistantConversation;
  }>(SEND_AI_ASSISTANT_MESSAGE);
  const [resolveMutation, { loading: resolving }] = useMutation<{
    resolveAiAssistantAction: AssistantConversation;
  }>(RESOLVE_AI_ASSISTANT_ACTION);
  const [deleteMutation] = useMutation(DELETE_MY_AI_ASSISTANT_CONVERSATION);
  const [stopMutation, { loading: stopping }] = useMutation<{
    stopAiAssistantTurn: AssistantConversation;
  }>(STOP_AI_ASSISTANT_TURN);

  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const accept = useCallback((next: AssistantConversation) => {
    const known = knownIds.current;
    cacheConversation(next);
    if (known.conversationId !== next.id) {
      // First sight of this conversation: never replay its old effects.
      knownIds.current = { conversationId: next.id, ids: new Set(next.messages.map((m) => m.id)) };
      setConversation(next);
      return;
    }
    const plan = planAssistantEffects(next.messages, known.ids);
    plan.seenIds.forEach((id) => known.ids.add(id));
    setConversation(next);
    if (plan.refresh) {
      if (isEditingSurface(pathRef.current)) {
        setReloadSuggested(true);
      } else {
        client.refetchQueries({ include: 'active' }).catch(() => {});
      }
    }
    if (plan.navigateTo && plan.navigateTo !== pathRef.current) navigate(plan.navigateTo);
  }, [client, navigate, cacheConversation]);

  useEffect(() => {
    if (data?.myAiAssistantConversation) accept(data.myAiAssistantConversation);
  }, [data, accept]);

  useEffect(() => {
    if (!conversationId) {
      setConversation(null);
      knownIds.current = { conversationId: null, ids: new Set() };
    }
    setReloadSuggested(false);
  }, [conversationId]);

  useEffect(() => {
    if (error && RESET_CODES.has(assistantErrorCode(error))) setConversationId(null);
  }, [error, setConversationId]);

  // The turn paused for calls this tab runs (e.g. editor edits). Another tab,
  // or this one after a reload, leaves them alone: they expire server-side.
  const awaitingHere = conversation?.status === 'awaiting_client'
    && ownsConversation(conversation.id);
  const working = conversation?.status === 'running' || awaitingHere;
  useEffect(() => {
    if (working && conversationId) startPolling(POLL_INTERVAL_MS);
    else stopPolling();
    return () => stopPolling();
  }, [working, conversationId, startPolling, stopPolling]);

  const pendingCalls = awaitingHere ? conversation?.pending_client_calls ?? [] : [];
  const pendingCallsKey = pendingCalls.map((call) => call.id).join(',');
  const pendingCallsRef = useRef(pendingCalls);
  pendingCallsRef.current = pendingCalls;
  useEffect(() => {
    const calls = pendingCallsRef.current;
    const id = conversation?.id;
    if (!id || !calls.length) return undefined;
    let cancelled = false;
    // The provider runs each call once, even if this panel remounts (e.g. the
    // assistant navigated); a later instance just keeps polling.
    runClientCalls(id, calls)
      .then((next) => { if (next && !cancelled) accept(next); })
      .catch((caught) => { if (!cancelled) onError(caught); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCallsKey]);

  const send = useCallback(async (
    text: string,
    attachments: Array<AssistantAttachment & { mime?: string; size?: number }> = [],
  ) => {
    setPending({ text, attachments: attachments.map(({ kind, url, name }) => ({ kind, url, name })) });
    try {
      const result = await sendMutation({
        variables: {
          conversationId,
          text,
          path: pathRef.current,
          clientContext: buildClientContext(),
          attachments: attachments.map(({
            kind, url, name, mime, size,
          }) => ({
            kind, url, name, mime, size,
          })),
        },
      });
      const next = result.data?.sendAiAssistantMessage;
      if (!next) return false;
      markConversationOwned(next.id);
      if (next.id !== conversationId) {
        // A brand-new conversation: everything in it is new from here on.
        knownIds.current = { conversationId: next.id, ids: new Set(next.messages.map((m) => m.id)) };
        setConversationId(next.id);
      }
      accept(next);
      return true;
    } catch (caught) {
      if (RESET_CODES.has(assistantErrorCode(caught)) && conversationId) setConversationId(null);
      onError(caught);
      return false;
    } finally {
      setPending(null);
    }
  }, [sendMutation, conversationId, setConversationId, accept, onError, buildClientContext, markConversationOwned]);

  const resolveAction = useCallback(async (actionId: string, approve: boolean) => {
    if (!conversationId) return;
    markConversationOwned(conversationId);
    try {
      const result = await resolveMutation({
        variables: {
          conversationId, actionId, approve, path: pathRef.current,
        },
      });
      if (result.data?.resolveAiAssistantAction) accept(result.data.resolveAiAssistantAction);
    } catch (caught) {
      onError(caught);
    }
  }, [resolveMutation, conversationId, accept, onError, markConversationOwned]);

  const stop = useCallback(async () => {
    if (!conversationId) return;
    try {
      const result = await stopMutation({ variables: { conversationId } });
      if (result.data?.stopAiAssistantTurn) accept(result.data.stopAiAssistantTurn);
    } catch (caught) {
      onError(caught);
    }
  }, [stopMutation, conversationId, accept, onError]);

  const remove = useCallback(async () => {
    if (!conversationId) return;
    try {
      await deleteMutation({ variables: { id: conversationId } });
      setConversationId(null);
    } catch (caught) {
      onError(caught);
    }
  }, [deleteMutation, conversationId, setConversationId, onError]);

  // While a conversation picked from history loads, show a skeleton rather
  // than the previous thread.
  const { current, loading, loadError } = resolveConversationView({
    conversationId,
    conversation,
    errorCode: error ? assistantErrorCode(error) : null,
    resetCodes: RESET_CODES,
  });

  return {
    conversation: current,
    loading,
    loadError,
    retry: () => { refetch().catch(() => {}); },
    working: !!current && working,
    ownsTurn: !!current && ownsConversation(current.id),
    sending,
    pending,
    resolving,
    stopping,
    reloadSuggested,
    dismissReload: () => setReloadSuggested(false),
    send,
    stop,
    resolveAction,
    remove,
  };
}
